import type { CourierInvoiceListItem } from '../../models/types';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { formatDate } from '@shared/utils/FormatUtils';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const statusVariant = (status: string | null | undefined): 'secondary' | 'outline' | 'default' => {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'secondary';
  if (s === 'imported') return 'default';
  return 'outline';
};

interface UseCourierInvoicesAgGridColumnsProps {
  basePath: string;
}

export const useCourierInvoicesAgGridColumns = ({ basePath }: UseCourierInvoicesAgGridColumnsProps) => {
  const { t } = useTranslation('courierInvoicesModule');

  const columnDefs: ColDef<CourierInvoiceListItem>[] = useMemo(() => {
    const statusLabel = (status: string | null | undefined) => {
      const key = (status || '').toLowerCase();
      if (key === 'draft') return t('status.draft');
      if (key === 'imported') return t('status.imported');
      return t('status.unknown', { status: status ?? '—' });
    };

    return [
      {
        field: 'id',
        headerName: t('list.columns.id'),
        minWidth: 100,
        maxWidth: 140,
        pinned: 'left',
        lockPosition: true,
        sortable: false,
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => {
          const row = params.data;
          if (!row?.id) return '—';
          return (
            <Link
              to={`${basePath}/${row.id}`}
              className="text-primary font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            >
              #{row.id}
            </Link>
          );
        },
      },
      {
        field: 'courier',
        headerName: t('list.columns.courier'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (p) => p.data?.courier?.name ?? '',
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => (
          <span className="text-foreground text-sm">{params.data?.courier?.name ?? '—'}</span>
        ),
      },
      {
        field: 'supplier_reference',
        headerName: t('list.columns.supplier_reference'),
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (p) => p.data?.supplier_reference ?? '',
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => (
          <span className="text-muted-foreground text-sm">{params.data?.supplier_reference ?? '—'}</span>
        ),
      },
      {
        colId: 'period',
        headerName: t('list.columns.period'),
        flex: 1.2,
        minWidth: 220,
        sortable: false,
        valueGetter: (p) =>
          p.data?.period_start || p.data?.period_end ? `${p.data?.period_start ?? ''}|${p.data?.period_end ?? ''}` : '',
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => {
          const row = params.data;
          if (!row?.period_start && !row?.period_end) {
            return <span className="text-muted-foreground text-sm">—</span>;
          }
          return (
            <span className="text-muted-foreground text-sm whitespace-nowrap">
              {row.period_start ? formatDate(row.period_start) : '—'} →{' '}
              {row.period_end ? formatDate(row.period_end) : '—'}
            </span>
          );
        },
      },
      {
        field: 'invoiced_at',
        headerName: t('list.columns.invoiced_at'),
        minWidth: 130,
        sortable: false,
        valueGetter: (p) => p.data?.invoiced_at ?? '',
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => (
          <span className="text-muted-foreground text-sm">
            {params.data?.invoiced_at ? formatDate(params.data.invoiced_at) : '—'}
          </span>
        ),
      },
      {
        field: 'status',
        headerName: t('list.columns.status'),
        minWidth: 130,
        sortable: false,
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => {
          const row = params.data;
          if (!row) return null;
          return (
            <Badge variant={statusVariant(row.status)} className="text-xs">
              {statusLabel(row.status)}
            </Badge>
          );
        },
      },
      {
        field: 'settlements_count',
        headerName: t('list.columns.settlements'),
        minWidth: 110,
        maxWidth: 140,
        sortable: false,
        type: 'numericColumn',
        cellClass: 'ag-right-aligned-cell',
        valueGetter: (p) => p.data?.settlements_count ?? null,
        cellRenderer: (params: { data?: CourierInvoiceListItem }) => (
          <span className="tabular-nums text-foreground text-sm">
            {params.data?.settlements_count != null ? params.data.settlements_count : '—'}
          </span>
        ),
      },
    ];
  }, [t, basePath]);

  return { columnDefs };
};
